import { EventEmitter } from 'events';
import TypedEventEmitter from 'typed-emitter';
import { DetectionResult, ProctorWebWorkers, UploadConfig } from './workerFarm';

export enum State {
	'Start' = 1,
	'Stop' = 2,
}

export interface SupportedEvents {
	'result': (result: DetectionResult) => void,
	'initComplete': () => void,
	'error': (error: Error) => void,
}

export class AIProctor extends (EventEmitter as new () => TypedEventEmitter<SupportedEvents>) {
	private videoElementRef: HTMLVideoElement;

	private state: State;

	private detectionInterval: NodeJS.Timer | null;

	private frequency: number;

	private useWebWorker: boolean;

	private initComplete: boolean;

	private uploadConfigSet: boolean;

	private canvas: OffscreenCanvas;

	private webWorker: null | ProctorWebWorkers;

	private sessionData: SessionData | null;

	constructor(videoElementRef: HTMLVideoElement, config: {
		frequency: number,
		webWorker: boolean,
	}) {
		super();
		this.useWebWorker = config.webWorker;
		this.webWorker = null;
		this.videoElementRef = videoElementRef;
		this.frequency = config.frequency;
		this.state = State.Stop;
		this.detectionInterval = null;
		this.initComplete = false;
		this.uploadConfigSet = false;
		this.canvas = new OffscreenCanvas(1080, 720);
		this.sessionData = null;
		this.init();
	}

	destory() {
		if (this.webWorker) {
			this.webWorker.terminate();
		}
	}

	static drawText(ctx: OffscreenCanvasRenderingContext2D, text: string, x: number, y: number) {
		ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
		const textWidth = ctx.measureText(text).width;
		const textHeight = 20;
		const padding = 5;
		ctx.fillRect(
			x - padding / 2,
			(y - textHeight / 2) - (padding / 2),
			textWidth + 2 * padding,
			textHeight + 2 * padding,
		);
		ctx.fillStyle = '#FFFFFF';
		ctx.font = '13px Arial';
		ctx.textBaseline = 'middle';
		ctx.fillText(text, x, y);
	}

	async init() {
		this.webWorker = new ProctorWebWorkers(1);
		this.webWorker.on('initComplete', () => {
			this.initalComplete();
		});
		this.webWorker.on('result', (result) => {
			this.emit('result', result);
		});
	}

	private initalComplete() {
		this.emit('initComplete');
		this.initComplete = true;
		this.tryStart();
	}

	private tryStart() {
		if (this.initComplete && this.uploadConfigSet && this.state !== State.Start) {
			this.setState(State.Start);
		}
	}

	setState(state: State) {
		if (this.detectionInterval) {
			clearInterval(this.detectionInterval);
		}
		if (state === State.Start) {
			this.detectionInterval = setInterval(() => {
				this.detect();
			}, this.frequency);
		} else {
			this.state = State.Stop;
			if (this.detectionInterval) {
				clearInterval(this.detectionInterval);
			}
		}
	}

	private detectWithWebWorker(imageToSend: ImageBitmap) {
		if (this.webWorker) {
			this.webWorker.detect(imageToSend);
		}
	}

	setUploadConfig(config: UploadConfig) {
		if (this.webWorker) {
			this.webWorker.sendConfig(config);
		}
		this.uploadConfigSet = true;
		this.tryStart();
	}

	setSessionData(data: SessionData | null) {
		this.sessionData = data;
	}

	detect() {
		if (!this.initComplete) {
			return;
		}
		const ctx = this.canvas.getContext('2d');
		if (!ctx) {
			return;
		}
		if ('drawImage' in ctx) {
			ctx.drawImage(this.videoElementRef, 0, 0, 1080, 720);
			ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
			const timestamp = new Date().toLocaleDateString('en-US', {
				weekday: 'long',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
				hourCycle: 'h12',
				month: 'long',
				year: 'numeric',
			});
			AIProctor.drawText(ctx, timestamp, 50, 60);
			if (this.sessionData) {
				AIProctor.drawText(ctx, `Name: ${this.sessionData.displayName}`, 50, 90);
				if (this.sessionData.enrollmentId) {
					AIProctor.drawText(ctx, `RollNo: ${this.sessionData.enrollmentId}`, 50, 120);
				} else {
					AIProctor.drawText(ctx, `Email: ${this.sessionData.email}`, 50, 120);
				}
			}
		}
		const bitmap = this.canvas.transferToImageBitmap();
		if (this.useWebWorker) {
			this.detectWithWebWorker(bitmap);
		}
	}
}
